'use client'

import { forwardRef, ReactNode, useState, useRef, useEffect } from 'react'
import { Loader2, Check, ArrowRight, Undo2 } from 'lucide-react'
import { 
  borderRadiusV3, 
  spacingV3, 
  shadowsV3, 
  animationsV3, 
  glassV3,
  componentSpacing,
  componentAnimations,
  componentShadows,
  typographyV3 
} from '@birhaus/tokens/v3'
import { cn } from '@birhaus/types/utils'

/**
 * BIRHAUS Button V3 - Radical Minimalist Design
 * 
 * Revolutionary button component with:
 * - Radical border radius (28px default)
 * - Glass morphism effects
 * - Micro-animations and hover states
 * - Generous spacing (2x traditional)
 * - Ripple effects on click
 * - Spanish-first labeling
 * - Accessibility optimized
 * - Performance optimized
 */


export interface BirhausButtonV3Props extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  // BIRHAUS Principle #7: Spanish-first labeling
  label?: string
  labelEs?: string
  labelEn?: string
  
  // V3 Design System - Appearance variants
  variant?: 'primary' | 'secondary' | 'ghost' | 'glass' | 'gradient' | 'outline'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  radius?: keyof typeof borderRadiusV3
  
  // V3 Enhanced states
  loading?: boolean
  loadingText?: string
  loadingTextEs?: string
  loadingTextEn?: string
  success?: boolean
  successText?: string
  successTextEs?: string
  successTextEn?: string
  
  // V3 Visual effects
  glow?: boolean
  float?: boolean
  pulse?: boolean
  ripple?: boolean
  
  // BIRHAUS Principle #5: Undo over confirm
  destructive?: boolean
  undoAction?: () => Promise<void>
  undoTimeoutMs?: number
  
  // Icon system
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  
  // Layout
  fullWidth?: boolean
  
  // Performance tracking
  trackPerformance?: boolean
  
  // Accessibility
  describedBy?: string
  describedByEs?: string
  describedByEn?: string
  
  children?: ReactNode
}

export const BirhausButtonV3 = forwardRef<HTMLButtonElement, BirhausButtonV3Props>(({
  // Content (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // V3 appearance
  variant = 'primary',
  size = 'md',
  radius = 'lg',
  
  // States
  loading = false,
  loadingText,
  loadingTextEs,
  loadingTextEn,
  success = false,
  successText,
  successTextEs,
  successTextEn,
  
  // V3 effects
  glow = false,
  float = false,
  pulse = false,
  ripple = true,
  
  // Undo functionality
  destructive = false,
  undoAction,
  undoTimeoutMs = 10000,
  
  // Icon
  icon,
  iconPosition = 'left',
  
  // Layout
  fullWidth = false,
  
  // Performance
  trackPerformance = false,
  
  // Accessibility
  describedBy,
  describedByEs,
  describedByEn,
  
  // Standard props
  className,
  disabled,
  onClick,
  children,
  ...props
}, ref) => {
  
  // State management
  const [isPressed, setIsPressed] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [showRipple, setShowRipple] = useState(false)
  const [ripplePosition, setRipplePosition] = useState({ x: 0, y: 0 })
  const [showUndoPrompt, setShowUndoPrompt] = useState(false)
  const [isUndoing, setIsUndoing] = useState(false)
  
  const buttonRef = useRef<HTMLButtonElement>(null)
  const undoTimeoutRef = useRef<NodeJS.Timeout>()
  const performanceStartRef = useRef<number>()
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayLabel = resolveText(labelEs, labelEn, label)
  const displayLoadingText = resolveText(
    loadingTextEs || 'Procesando...',
    loadingTextEn || 'Processing...',
    loadingText
  )
  const displaySuccessText = resolveText(
    successTextEs || 'Completado',
    successTextEn || 'Completed',
    successText
  )
  const displayDescription = resolveText(describedByEs, describedByEn, describedBy)
  
  // V3 Size classes with generous padding
  const sizeClasses = {
    xs: `h-10 px-5 text-sm gap-2 rounded-[${borderRadiusV3.sm}]`,
    sm: `h-12 px-6 text-sm gap-2.5 rounded-[${borderRadiusV3.md}]`,
    md: `h-14 px-8 text-base gap-3 rounded-[${borderRadiusV3.lg}]`,
    lg: `h-16 px-10 text-lg gap-3.5 rounded-[${borderRadiusV3.xl}]`,
    xl: `h-20 px-12 text-xl gap-4 rounded-[${borderRadiusV3['2xl']}]`,
  }
  
  // V3 Variant classes with radical design
  const variantClasses = {
    primary: `
      bg-gradient-to-r from-blue-600 to-blue-500 text-white 
      hover:from-blue-700 hover:to-blue-600
      active:from-blue-800 active:to-blue-700
      focus:ring-4 focus:ring-blue-400/30
      shadow-lg hover:shadow-xl
      ${glow ? 'shadow-[0_0_40px_-10px_rgba(31,111,235,0.25)]' : ''}
    `,
    
    secondary: `
      bg-white text-gray-700 border-2 border-gray-200
      hover:bg-gray-50 hover:border-gray-300
      active:bg-gray-100
      focus:ring-4 focus:ring-gray-400/30
      shadow-md hover:shadow-lg
    `,
    
    ghost: `
      bg-transparent text-gray-600
      hover:bg-gray-100/50 hover:text-gray-900
      active:bg-gray-200/50
      focus:ring-4 focus:ring-gray-400/30
    `,
    
    glass: `
      ${glassV3.effects.medium.background ? `bg-[${glassV3.effects.medium.background}]` : 'bg-white/50'}
      backdrop-blur-lg border border-white/30
      text-gray-700 hover:bg-white/60
      focus:ring-4 focus:ring-blue-400/30
      shadow-lg hover:shadow-xl
    `,
    
    gradient: `
      bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600
      text-white 
      hover:from-purple-700 hover:via-blue-700 hover:to-cyan-700
      focus:ring-4 focus:ring-purple-400/30
      shadow-xl hover:shadow-2xl
      ${glow ? 'shadow-[0_0_40px_-10px_rgba(139,92,246,0.25)]' : ''}
    `,
    
    outline: `
      bg-transparent border-2 border-gray-300 text-gray-700
      hover:border-gray-400 hover:bg-gray-50/50
      active:bg-gray-100/50
      focus:ring-4 focus:ring-gray-400/30
    `,
  }
  
  // Handle ripple effect
  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (ripple) {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (rect) {
        setRipplePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        })
        setShowRipple(true)
        setTimeout(() => setShowRipple(false), 600)
      }
    }
    
    setIsPressed(true)
    
    if (trackPerformance) {
      performanceStartRef.current = performance.now()
    }
  }
  
  const handleMouseUp = () => {
    setIsPressed(false)
    
    if (trackPerformance && performanceStartRef.current) {
      const duration = performance.now() - performanceStartRef.current
      console.log(`[BIRHAUS v3] Button interaction took ${duration.toFixed(2)}ms`)
    }
  }
  
  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => {
    setIsHovered(false)
    setIsPressed(false)
  }
  
  // Handle destructive action with undo
  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (destructive && undoAction && !showUndoPrompt) {
      // Execute destructive action immediately, then show undo option
      await onClick?.(event)
      
      setShowUndoPrompt(true)
      
      // Set timeout to hide undo option
      undoTimeoutRef.current = setTimeout(() => {
        setShowUndoPrompt(false)
      }, undoTimeoutMs)
      
      return
    }
    
    // Regular action
    onClick?.(event)
  }
  
  // Handle undo action
  const handleUndo = async () => {
    if (!undoAction) return
    
    setIsUndoing(true)
    
    try {
      await undoAction()
      setShowUndoPrompt(false)
      
      // Clear timeout
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current)
      }
    } catch (error) {
      console.error('BIRHAUS v3: Undo failed:', error)
    } finally {
      setIsUndoing(false)
    }
  }
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current)
      }
    }
  }, [])
  
  // Show undo prompt instead of button when destructive action executed
  if (showUndoPrompt) {
    return (
      <div className={cn(
        'inline-flex items-center gap-3 p-4',
        `rounded-[${borderRadiusV3.lg}]`,
        'bg-red-50/90 backdrop-blur-sm border border-red-200/50',
        'shadow-lg animate-in slide-in-from-bottom-2 duration-300'
      )}>
        <div className="flex items-center gap-2 text-red-800">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-medium">Acción completada</span>
        </div>
        
        <button
          onClick={handleUndo}
          disabled={isUndoing}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium',
            `rounded-[${borderRadiusV3.md}]`,
            'text-red-700 bg-white/80 border border-red-300',
            'hover:bg-red-50 hover:border-red-400',
            'focus:ring-2 focus:ring-red-400/30 focus:outline-none',
            'transition-all duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <Undo2 className="h-3 w-3" />
          {isUndoing ? 'Deshaciendo...' : 'Deshacer'}
        </button>
      </div>
    )
  }
  
  const baseClasses = cn(
    // Base styles with v3 principles
    'relative inline-flex items-center justify-center',
    'font-medium',
    'transition-all duration-300 ease-out',
    'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-1',
    'disabled:pointer-events-none disabled:opacity-50',
    'overflow-hidden', // For ripple effect
    'transform-gpu', // GPU acceleration
    
    // Size with generous padding
    sizeClasses[size],
    
    // Variant styling
    variantClasses[variant],
    
    // Width
    fullWidth && 'w-full',
    
    // Interactive effects
    isPressed && 'scale-[0.98]',
    float && isHovered && '-translate-y-1',
    
    // Animation effects
    pulse && 'animate-pulse',
    
    className
  )
  
  return (
    <button
      ref={ref || buttonRef}
      className={baseClasses}
      disabled={disabled || loading}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      aria-describedby={displayDescription ? `${props.id}-desc` : undefined}
      data-birhaus-v3="button"
      data-variant={variant}
      data-size={size}
      {...props}
    >
      {/* Ripple effect */}
      {showRipple && ripple && (
        <span
          className="absolute animate-ping"
          style={{
            left: ripplePosition.x,
            top: ripplePosition.y,
            width: '20px',
            height: '20px',
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            backgroundColor: variant === 'primary' || variant === 'gradient' 
              ? 'rgba(255, 255, 255, 0.5)' 
              : 'rgba(0, 0, 0, 0.1)',
            animation: 'ripple 600ms ease-out',
            pointerEvents: 'none',
          }}
        />
      )}
      
      {/* Loading spinner */}
      {loading && (
        <Loader2 className="animate-spin" style={{ width: '1.25rem', height: '1.25rem' }} />
      )}
      
      {/* Success checkmark */}
      {success && !loading && (
        <Check className="text-green-500" style={{ width: '1.25rem', height: '1.25rem' }} />
      )}
      
      {/* Left icon */}
      {icon && iconPosition === 'left' && !loading && !success && (
        <span className="flex-shrink-0">{icon}</span>
      )}
      
      {/* Button text */}
      <span className="relative font-medium">
        {loading 
          ? displayLoadingText
          : success 
            ? displaySuccessText
            : children || displayLabel
        }
      </span>
      
      {/* Right icon */}
      {icon && iconPosition === 'right' && !loading && !success && (
        <span className="flex-shrink-0">{icon}</span>
      )}
      
      {/* Default arrow for primary buttons without custom icon */}
      {variant === 'primary' && !icon && !loading && !success && (
        <ArrowRight 
          className="ml-1 transition-transform group-hover:translate-x-1" 
          style={{ width: '1rem', height: '1rem' }}
        />
      )}
      
      {/* Accessibility description */}
      {displayDescription && (
        <span id={`${props.id}-desc`} className="sr-only">
          {displayDescription}
        </span>
      )}
    </button>
  )
})

BirhausButtonV3.displayName = 'BirhausButtonV3'

// Export hook for managing v3 button state
export function useBirhausButtonV3() {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const executeAction = async (action: () => Promise<void>) => {
    setLoading(true)
    setError(null)
    
    try {
      await action()
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
      console.error('BIRHAUS v3: Button action failed:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }
  
  return {
    loading,
    success,
    error,
    executeAction,
    setLoading,
    setSuccess,
    setError,
  }
}